import { Check, ChevronDown } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";
import { localeLabels, localeNames, locales } from "../../content/config";
import { isLocale, localizedPath, pathWithoutLocale } from "../../lib/routes";
import { track } from "../../lib/analytics";
import type { Locale } from "../../types/content";
import { CountryFlag } from "../ui/CountryFlag";

const optionWidth = "w-[208px]";

export function LanguageSwitcher() {
  const { locale } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const activeLocale = isLocale(locale) ? locale : "en";
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocPointer(e: MouseEvent | TouchEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onDocKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    document.addEventListener("keydown", onDocKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      document.removeEventListener("keydown", onDocKey);
    };
  }, []);

  useEffect(() => {
    if (!open || !menuRef.current) return;
    const active = menuRef.current.querySelector<HTMLButtonElement>('[role="option"][aria-selected="true"]');
    (active ?? menuRef.current.querySelector<HTMLButtonElement>('[role="option"]'))?.focus();
  }, [open]);

  function select(next: Locale) {
    setOpen(false);
    track("language_changed", { locale: next });
    navigate(localizedPath(next, pathWithoutLocale(location.pathname)));
  }

  function onRootKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (!open) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
      if (!buttons.length) return;
      const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const next = current === -1 ? 0 : e.key === "ArrowDown" ? (current + 1) % buttons.length : (current - 1 + buttons.length) % buttons.length;
      buttons[next].focus();
    }
  }

  return (
    <div ref={rootRef} className="relative" onKeyDown={onRootKeyDown}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        className={`flex min-h-12 items-center gap-2.5 rounded-[14px] border border-line bg-surface px-4 text-sm font-semibold text-ink-primary shadow-[0_1px_0_rgba(255,255,255,0.6)_inset] transition-colors hover:border-brand-blue/40 hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40 focus-visible:ring-offset-[3px]`}
      >
        <CountryFlag locale={activeLocale} />
        <span>{localeLabels[activeLocale]}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-ink-secondary transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <motion.div
          ref={menuRef}
          role="listbox"
          aria-label="Language"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.18, ease: "easeOut" }}
          className={`absolute end-0 top-[calc(100%+8px)] z-50 ${optionWidth} overflow-hidden rounded-[14px] border border-[rgba(27,47,58,0.1)] bg-[rgba(249,247,241,0.96)] p-1.5 shadow-[0_18px_45px_rgba(20,35,45,0.16),0_3px_10px_rgba(20,35,45,0.08)] backdrop-blur-[18px]`}
        >
          {locales.map((item) => {
            const active = item === activeLocale;
            return (
              <button
                key={item}
                type="button"
                role="option"
                aria-selected={active}
                aria-label={localeNames[item]}
                onClick={() => select(item)}
                className={`flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-start transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/50 ${active ? "bg-[linear-gradient(90deg,rgba(53,104,232,0.10),rgba(53,104,232,0.035))]" : "hover:bg-[rgba(20,42,51,0.055)]"}`}
              >
                <CountryFlag locale={item} />
                <span className="flex-1">
                  <span className={`block text-sm font-semibold leading-tight ${active ? "text-brand-blue" : "text-ink-primary"}`}>{localeNames[item]}</span>
                  <span className="block text-[0.68rem] font-medium text-ink-secondary">{localeLabels[item]}</span>
                </span>
                {active && <Check className="h-4 w-4 shrink-0 text-brand-blue" aria-hidden="true" />}
              </button>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
